import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "../components/AdminLayout";
import { OperationsHealthCard } from "../components/OperationsHealthCard";
import { api, fileUrl } from "../lib/api";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Activity,
  CheckCircle2,
  Clock,
  MapPin,
  TrendingUp,
  AlertCircle,
  Users,
} from "lucide-react";
import { fmtTime } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";

const REFRESH_MS = 30_000;

function brl(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

export default function TodayOverview() {
  const { t } = useI18n();
  const [data, setData] = useState({
    active_jobs: [],
    upcoming_jobs: [],
    finished_count: 0,
    total_count: 0,
    idle_teams: [],
    revenue_done: 0,
    revenue_potential: 0,
  });
  const [alerts, setAlerts] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [{ data }, alertsRes] = await Promise.all([
          api.get("/today-overview"),
          api.get("/today/alerts").catch(() => ({ data: null })),
        ]);
        if (alive) {
          setData(data);
          setAlerts(alertsRes.data);
          setLoaded(true);
        }
      } catch {
        if (alive) setLoaded(true);
      }
    }
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const activeWithCoords = data.active_jobs.filter(
    (j) => j.start_lat && j.start_lng,
  );
  const mapCenter = activeWithCoords[0]
    ? [activeWithCoords[0].start_lat, activeWithCoords[0].start_lng]
    : [42.3, -71.05];

  const progress =
    data.total_count > 0
      ? Math.round((data.finished_count / data.total_count) * 100)
      : 0;

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="today-overview-page">
        {/* Header */}
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Operações")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Activity size={26} className="text-blue-600" /> {t("Resumo do dia")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {loaded ? t("Atualiza automaticamente a cada 30s.") : t("Carregando dados…")}
          </p>
        </div>

        {/* Proactive alerts */}
        {alerts && alerts.total > 0 && <AlertsPanel alerts={alerts} />}

        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Activity}
            color="#8B5CF6"
            label={t("Em andamento")}
            value={data.active_jobs.length}
            testId="today-stat-active"
          />
          <StatCard
            icon={CheckCircle2}
            color="#10B981"
            label={t("Concluídas")}
            value={`${data.finished_count}/${data.total_count}`}
            testId="today-stat-finished"
          />
          <StatCard
            icon={Clock}
            color="#2563EB"
            label={t("Próximas")}
            value={data.upcoming_jobs.length}
            testId="today-stat-upcoming"
          />
          <StatCard
            icon={TrendingUp}
            color="#059669"
            label={t("Faturamento")}
            value={brl(data.revenue_done)}
            hint={`${t("de")} ${brl(data.revenue_potential)} ${t("previsto")}`}
            testId="today-stat-revenue"
          />
        </div>

        {/* Progress bar */}
        <div className="cm-card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              {t("Progresso do dia")}
            </span>
            <span className="text-sm font-bold text-slate-800">{progress}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="today-progress-bar"
            />
          </div>
        </div>

        {/* Mapa + Active jobs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 cm-card p-3 overflow-hidden" data-testid="today-map">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2 px-2">
              {t("Mapa ao vivo")} · {activeWithCoords.length} {t("equipe(s) em campo")}
            </div>
            <div className="h-[300px] sm:h-[400px] rounded-lg overflow-hidden">
              <LiveMap
                center={mapCenter}
                activeJobs={activeWithCoords}
                upcomingJobs={data.upcoming_jobs}
              />
            </div>
          </div>

          {/* Próximas 3 limpezas */}
          <div className="cm-card p-4" data-testid="today-upcoming-list">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-3">
              {t("Próximas limpezas")}
            </div>
            {data.upcoming_jobs.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                {t("Nenhuma limpeza agendada nas próximas horas.")}
              </div>
            ) : (
              <ul className="space-y-2">
                {data.upcoming_jobs.map((j) => (
                  <li key={j.id}>
                    <Link
                      to={`/jobs/${j.id}`}
                      data-testid={`today-upcoming-${j.id}`}
                      className="block p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <strong className="text-sm text-slate-800">
                          {fmtTime(j.scheduled_at)}
                        </strong>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                          style={{
                            background: `${j.team_color || "#2563EB"}1A`,
                            color: j.team_color || "#2563EB",
                          }}
                        >
                          {j.team_name || t("Equipe")}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 mt-1 truncate">
                        {j.client_name || "—"}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
                        <MapPin size={10} /> {j.client_address || t("Endereço não informado")}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Equipes ociosas */}
        {data.idle_teams.length > 0 && (
          <div
            className="cm-card p-4 border-amber-200 bg-amber-50/40"
            data-testid="today-idle-teams"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <AlertCircle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900">
                  {data.idle_teams.length} {data.idle_teams.length > 1 ? t("equipes sem job ativo ou agendado nas próximas 2h") : t("equipe sem job ativo ou agendado nas próximas 2h")}
                </div>
                <div className="text-sm text-amber-800/80 mt-1">
                  {t("Considere distribuir uma limpeza não agendada ou liberar o time.")}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {data.idle_teams.map((team) => (
                    <span
                      key={team.id}
                      data-testid={`today-idle-team-${team.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white border"
                      style={{ borderColor: team.color || "#cbd5e1" }}
                    >
                      <Users size={11} />
                      {team.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// Memoiza o mapa para não re-inicializar o container do Leaflet a cada poll.
// Só re-renderiza quando o conjunto de IDs muda.
const LiveMap = React.memo(
  function LiveMap({ center, activeJobs, upcomingJobs }) {
    return (
      <MapContainer
        center={center}
        zoom={11}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {activeJobs.map((j) => (
          <Marker key={j.id} position={[j.start_lat, j.start_lng]}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <strong>{j.team_name || "Equipe"}</strong>
                <br />
                em {j.client_name || "—"}
                {Array.isArray(j.team_members) && j.team_members.length > 0 && (
                  <div style={{ display: "flex", marginTop: 6, gap: 4, alignItems: "center" }}>
                    {j.team_members.slice(0, 4).map((m) => (
                      <div
                        key={m.id}
                        title={m.name}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: j.team_color || "#2563EB",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          border: "2px solid #fff",
                          boxShadow: "0 1px 2px rgba(0,0,0,.15)",
                          overflow: "hidden",
                        }}
                      >
                        {m.photo_path ? (
                          <img
                            src={fileUrl(m.photo_path)}
                            alt={m.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          (m.name || "?").split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase()
                        )}
                      </div>
                    ))}
                    {j.team_members.length > 4 && (
                      <span style={{ fontSize: 11, color: "#64748b", marginLeft: 4 }}>
                        +{j.team_members.length - 4}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ marginTop: 6 }}>
                  <Link to={`/jobs/${j.id}`}>Abrir job →</Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        {upcomingJobs
          .filter((j) => j.client_lat && j.client_lng)
          .map((j) => (
            <CircleMarker
              key={j.id}
              center={[j.client_lat, j.client_lng]}
              radius={6}
              pathOptions={{
                color: j.team_color || "#2563EB",
                fillColor: j.team_color || "#2563EB",
                fillOpacity: 0.5,
              }}
            >
              <Popup>
                <strong>{fmtTime(j.scheduled_at)}</strong>
                <br />
                {j.client_name || "—"} · {j.team_name || "—"}
              </Popup>
            </CircleMarker>
          ))}
      </MapContainer>
    );
  },
  (prev, next) => {
    const ids = (arr) => arr.map((j) => j.id).join(",");
    return (
      ids(prev.activeJobs) === ids(next.activeJobs) &&
      ids(prev.upcomingJobs) === ids(next.upcomingJobs)
    );
  },
);

function StatCard({ icon: Icon, color, label, value, hint, testId }) {
  return (
    <div className="cm-card p-3 sm:p-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-semibold leading-tight">
            {label}
          </div>
          <div className="mt-1 text-xl sm:text-2xl font-bold text-slate-900 truncate">
            {value}
          </div>
          {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
        </div>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}1A`, color }}
        >
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}


function AlertsPanel({ alerts }) {
  const { t } = useI18n();
  const groups = [
    {
      key: "no-team",
      title: t("Limpezas sem equipe"),
      items: alerts.jobs_without_team,
      tone: "rose",
      hint: t("Aloque uma equipe antes do horário"),
      link: "/schedule",
      itemLabel: (it) => `${it.client_name} · ${fmtTime(it.scheduled_at)}`,
      itemHref: (it) => `/jobs/${it.job_id}`,
    },
    {
      key: "overdue-start",
      title: t("Atrasadas (não iniciaram)"),
      items: alerts.overdue_start,
      tone: "amber",
      hint: t("Ligue para o colaborador e confirme se vai começar"),
      link: "/schedule",
      itemLabel: (it) =>
        `${it.client_name} · ${fmtTime(it.scheduled_at)}${it.team_name ? ` · ${it.team_name}` : ""}`,
      itemHref: (it) => `/jobs/${it.job_id}`,
    },
    {
      key: "overdue-invoices",
      title: t("Faturas vencidas"),
      items: alerts.overdue_invoices,
      tone: "rose",
      hint: t("Envie cobrança ou link de pagamento"),
      link: "/recebimentos",
      itemLabel: (it) =>
        `${it.client_name} · ${brl(it.amount_due)} (${t("vence")} ${(it.due_date || "").slice(0, 10)})`,
      itemHref: () => "/recebimentos",
    },
    {
      key: "pending-bookings",
      title: t("Agendamentos pendentes"),
      items: alerts.pending_bookings,
      tone: "blue",
      hint: t("Cliente pediu via portal — aprove ou recuse"),
      link: "/booking-requests",
      itemLabel: (it) =>
        `${it.client_name}${it.preferred_date ? ` · ${it.preferred_date.slice(0, 10)}` : ""}`,
      itemHref: () => "/booking-requests",
    },
  ].filter((g) => (g.items || []).length > 0);

  return (
    <div
      data-testid="today-alerts-panel"
      className="cm-card p-0 overflow-hidden border-2 border-amber-200"
    >
      <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
        <AlertCircle size={18} className="text-amber-600" />
        <div className="text-sm font-bold text-amber-900">
          {alerts.total} {alerts.total > 1 ? t("alertas requerem sua atenção") : t("alerta requer sua atenção")}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
        {groups.map((g) => (
          <AlertGroup key={g.key} group={g} />
        ))}
      </div>
    </div>
  );
}

function AlertGroup({ group }) {
  const { t } = useI18n();
  const palette = {
    rose: { dot: "bg-rose-500", text: "text-rose-700", chip: "bg-rose-50 text-rose-700" },
    amber: { dot: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-50 text-amber-700" },
    blue: { dot: "bg-blue-500", text: "text-blue-700", chip: "bg-blue-50 text-blue-700" },
  }[group.tone];
  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${palette.dot}`} />
          <div className={`font-semibold text-sm ${palette.text}`}>{group.title}</div>
          <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${palette.chip}`}>
            {group.items.length}
          </span>
        </div>
        {group.link && (
          <Link
            to={group.link}
            data-testid={`alerts-group-${group.key}-link`}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-900"
          >
            {t("Ver tudo →")}
          </Link>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-2">{group.hint}</div>
      <ul className="space-y-1">
        {group.items.slice(0, 4).map((it, i) => (
          <li key={it.id || `${group.key}-${i}`}>
            <Link
              to={group.itemHref(it)}
              data-testid={`alerts-item-${group.key}-${i}`}
              className="block text-xs text-slate-700 hover:text-blue-700 hover:underline truncate"
            >
              {group.itemLabel(it)}
            </Link>
          </li>
        ))}
        {group.items.length > 4 && (
          <li className="text-[11px] text-slate-500">
            +{group.items.length - 4} {t("outros")}
          </li>
        )}
      </ul>
    </div>
  );
}
